import type { StudioStatusKind } from '../../pages/moo/studio-components';
import { toUniversalUtcDate } from '../../utils/date.utils';

export type ConnectorStatus =
  | 'Connected'
  | 'SUCCESS'
  | 'INITIALIZING'
  | 'FAILURE'
  | 'COMING_SOON'
  | 'AVAILABLE';

export type Connector = {
  connectorType: string;
  connectorName: string;
  name: string;
  description: string;
  connectorListStatus: string;
  vendor?: string;
  logoUrl?: string;
  /** Catalog grouping, such as security or AI. Display text, owned by the catalog. */
  category?: string;
  provider: 'COWBELL' | 'LEEN';
  hidden?: boolean;
};

export interface Connection {
  id: string;
  accountId: string;
  cbid: string;
  createdByUser: string;
  createdByUsername: string;
  createdByTimestamp: string;
  updatedByTimestamp: string;
  updatedByUser: string;
  updatedByUsername: string;
  connectorStatus: ConnectorStatus;
  lastPull: string | null;
  connectorType: string;
  connectorName: string;
  credentials: unknown;
  metadata: { id: string; vendor: string } | null;
  name: string;
  vendor?: string;
  description: string;
  logoUrl?: string;
  category?: string;
  connectorListStatus: ConnectorStatus;
  provider: 'COWBELL' | 'LEEN';
}

export interface StatusBadgeValue {
  readonly kind: StudioStatusKind;
  readonly label: string;
}

/** Connector types the page never lists, whichever section they would land in. */
const NEVER_LISTED = ['GOOGLE', 'LEEN'];

/** Cowbell-provided types whose tile is retired; existing connections still show. */
const RETIRED_COWBELL_TILES = ['WIZER'];

const sameType = (a: string, b: string) =>
  a.trim().toLowerCase() === b.trim().toLowerCase();

export const matchesSearch = (name: string, search: string): boolean =>
  name.toLowerCase().includes(search.trim().toLowerCase());

/** Joins each connection to its catalog entry for the display name and copy. */
export const enrichConnections = (
  connections: Connection[],
  connectors: Connector[]
): Connection[] =>
  connections.map((connection) => {
    const connector = connectors.find((candidate) =>
      sameType(candidate.connectorType, connection.connectorType)
    );
    if (!connector) {
      throw new Error(
        `No matching connector found for connectorType: ${connection.connectorType}`
      );
    }
    return {
      ...connection,
      name: connector.name,
      description: connector.description,
      logoUrl: connector.logoUrl,
      category: connector.category,
      connectorListStatus: 'Connected',
    };
  });

/** One entry per connector type; a type can hold several connections. */
export const groupConnectionsByType = (
  connections: Connection[]
): Connection[][] => {
  const groups = new Map<string, Connection[]>();
  connections.forEach((connection) => {
    const group = groups.get(connection.connectorType) ?? [];
    group.push(connection);
    groups.set(connection.connectorType, group);
  });
  return Array.from(groups.values());
};

export const visibleConnected = (
  groups: Connection[][],
  search: string
): Connection[][] =>
  groups.filter(([first]) => {
    if (NEVER_LISTED.includes(first.connectorType)) return false;
    return (
      matchesSearch(first.name ?? first.connectorType, search) ||
      matchesSearch(first.category ?? '', search)
    );
  });

/** The catalog minus hidden entries and anything already connected. */
export const availableConnectors = (
  connections: Connection[],
  connectors: Connector[]
): Connector[] =>
  connectors.filter(
    (connector) =>
      !connector.hidden &&
      !connections.some((connection) =>
        sameType(connector.connectorType, connection.connectorType)
      )
  );

export const visibleAvailable = (
  connectors: Connector[],
  search: string
): Connector[] =>
  connectors.filter((connector) => {
    const { connectorType = '', provider, name } = connector;
    if (NEVER_LISTED.includes(connectorType)) return false;
    if (provider === 'COWBELL' && RETIRED_COWBELL_TILES.includes(connectorType))
      return false;
    if (provider !== 'COWBELL' && provider !== 'LEEN') return false;
    return (
      matchesSearch(name ?? '', search) ||
      matchesSearch(connector.category ?? '', search)
    );
  });

const categoryKey = (category: string | undefined) => category?.trim() ?? '';

/**
 * Category display order (keys as `categoryKey` gives them): the category with
 * the fewest connectors first, so a small category (today, AI) is not buried
 * under a large one. Uncategorised entries count as one group of their own.
 * Ties keep first-seen order. Computed over the whole catalog, not search
 * results, so tiles do not reorder while someone types.
 */
export const categoryOrder = (
  categories: ReadonlyArray<string | undefined>
): string[] => {
  const counts = new Map<string, number>();
  categories.forEach((category) => {
    const key = categoryKey(category);
    counts.set(key, (counts.get(key) ?? 0) + 1);
  });
  return Array.from(counts.entries())
    .map(([key, count], seen) => ({ key, count, seen }))
    .sort((a, b) => a.count - b.count || a.seen - b.seen)
    .map(({ key }) => key);
};

/**
 * Entries reordered so the categories earliest in `order` lead, keeping the
 * catalog's own order within a category. Categories missing from `order`
 * follow, in their original order.
 */
export const sortByCategory = <T>(
  items: readonly T[],
  categoryOf: (item: T) => string | undefined,
  order: readonly string[]
): T[] => {
  const rank = (item: T) => {
    const index = order.indexOf(categoryKey(categoryOf(item)));
    return index === -1 ? order.length : index;
  };
  return items
    .map((item, seen) => ({ item, seen, rank: rank(item) }))
    .sort((x, y) => x.rank - y.rank || x.seen - y.seen)
    .map(({ item }) => item);
};

/**
 * The tag a tile carries to stand out from its neighbours. The largest group,
 * uncategorised entries included, is the baseline and goes untagged; every
 * other category is named on its tiles.
 */
export const categoryTag = (
  category: string | undefined,
  order: readonly string[]
): string | undefined => {
  const key = categoryKey(category);
  if (!key) return undefined;
  return key === order[order.length - 1] ? undefined : key;
};

export interface EmptyCopy {
  readonly title: string;
  readonly body?: string;
}

const EMPTY_COPY: Record<
  'connected' | 'available',
  Record<'searching' | 'idle', EmptyCopy>
> = {
  connected: {
    searching: { title: 'No connected tools match your search' },
    idle: {
      title: 'No connectors yet',
      body: 'Connect a tool from the list below to share its security data with Cowbell.',
    },
  },
  available: {
    searching: { title: 'No connectors match your search' },
    idle: { title: 'Every available connector is connected' },
  },
};

/** What an empty section says, which depends on whether a search emptied it. */
export const emptyCopy = (
  section: 'connected' | 'available',
  search: string
): EmptyCopy => EMPTY_COPY[section][search.trim() ? 'searching' : 'idle'];

/** The secondary line under a connected tool's name. */
export const syncSubtitle = (
  lastPull: string | null | undefined,
  ready: boolean
): string | undefined => {
  if (lastPull) return `Last synced ${toUniversalUtcDate(lastPull)}`;
  return ready ? undefined : 'Waiting for the first sync';
};

const CONNECTION_STATUS: Partial<Record<string, StatusBadgeValue>> = {
  SUCCESS: { kind: 'active', label: 'Connected' },
  Connected: { kind: 'active', label: 'Connected' },
  FAILURE: { kind: 'error', label: 'Failed' },
};

const INITIALIZING: StatusBadgeValue = {
  kind: 'attention',
  label: 'Initializing',
};

export const connectionStatusBadge = (status: string): StatusBadgeValue =>
  CONNECTION_STATUS[status] ?? INITIALIZING;

/** `COMING_SOON` -> `Coming soon`. Only non-AVAILABLE catalog states reach here. */
export const catalogStatusLabel = (status: string): string => {
  const words = status.replace(/_/g, ' ').toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
};
